import { memo, useCallback } from 'react';

import { MoveOutline, Trash2Outline } from '@lyearn/icons';
import { IconButton, Input, Stack, Typography } from '@lyearn/ui';
import IconSettingsColorPicker from '@/components/IconSettingsColorPicker';
import { IconSettingsInput } from '@/types/schema';

import { ProjectEditorStageFormValue } from './Editor/config';

interface StageInputProps {
  value: ProjectEditorStageFormValue;
  error?: string;
  onChange: (stage: ProjectEditorStageFormValue) => void;
  onDelete: () => void;
  onMouseEnterDragHandle?: React.MouseEventHandler<HTMLElement>;
  onMouseLeaveDragHandle?: React.MouseEventHandler<HTMLElement>;
}

function StageInput({
  value,
  error,
  onChange,
  onDelete,
  onMouseEnterDragHandle,
  onMouseLeaveDragHandle,
}: StageInputProps) {
  const onChangeName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (newValue.length <= 40) {
        onChange({ ...value, name: newValue });
      }
    },
    [onChange, value],
  );
  const onChangeIconSettings = useCallback(
    (iconSettings: IconSettingsInput) => {
      onChange({ ...value, iconSettings });
    },
    [onChange, value],
  );

  return (
    <Stack className="w-full" direction="col">
      <Stack alignItems="center" className="group relative w-full" direction="row" gap={0}>
        <IconButton
          Icon={MoveOutline}
          className="absolute left-0 -translate-x-full shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
          size="xs"
          onMouseEnter={onMouseEnterDragHandle}
          onMouseLeave={onMouseLeaveDragHandle}
        />
        <Input
          className="flex-1"
          endAdornment={
            <Trash2Outline
              className="h-24 w-24 p-2 cursor-pointer opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 text-icon-placeholder hover:text-icon-error-light"
              onClick={onDelete}
            />
          }
          error={!!error}
          placeholder="Stage Name"
          size="large"
          startAdornment={
            <IconSettingsColorPicker value={value.iconSettings} onChange={onChangeIconSettings} />
          }
          type="text"
          value={value.name}
          onChange={onChangeName}
        />
      </Stack>
      {error ? (
        <Typography className="mt-4 ms-8" color="text-text-error" variant="body-short-03">
          {error}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default memo(StageInput);
